import { useState } from 'react';
import { AdvancedSettings } from '../types';

interface AdvancedSettingsProps {
  settings: AdvancedSettings;
  onChange: (s: AdvancedSettings) => void;
}

const selectClass = 'w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-stone-300 focus:outline-none focus:border-orange-500/60 cursor-pointer transition-colors duration-200';

function ToggleRow({ label, value, onChange, hint }: { label: string; value: boolean; onChange: (v: boolean) => void; hint?: string }) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-stone-800 last:border-b-0">
      <div>
        <div className="text-sm text-stone-300 font-medium">{label}</div>
        {hint && <div className="text-xs text-stone-600 mt-0.5">{hint}</div>}
      </div>
      <button
        onClick={() => onChange(!value)}
        className={`w-10 h-6 rounded-full relative transition-all duration-300 shrink-0 cursor-pointer ${value ? 'bg-orange-500' : 'bg-stone-700'}`}
      >
        <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all duration-300 ${value ? 'left-[18px]' : 'left-0.5'}`} />
      </button>
    </div>
  );
}

export default function AdvancedSettingsSection({ settings, onChange }: AdvancedSettingsProps) {
  const [open, setOpen] = useState(false);

  const set = <K extends keyof AdvancedSettings>(key: K, val: AdvancedSettings[K]) => {
    onChange({ ...settings, [key]: val });
  };

  return (
    <div className="bg-stone-900 border border-stone-800 rounded-xl mb-4 overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-stone-800/40 transition-colors duration-200"
      >
        <div className="flex items-center gap-2.5">
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-settings-4-line text-stone-400 text-sm" />
          </div>
          <span className="text-sm font-semibold text-white">Advanced Settings</span>
          <span className="text-xs text-stone-600 bg-stone-800 px-2 py-0.5 rounded-full">Optional</span>
        </div>
        <div className="w-5 h-5 flex items-center justify-center">
          {open ? <i className="ri-arrow-up-s-line text-stone-500" /> : <i className="ri-arrow-down-s-line text-stone-500" />}
        </div>
      </button>

      {open && (
        <div className="px-5 pb-5 border-t border-stone-800">
          <div className="grid grid-cols-1 gap-4 mt-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wide mb-1.5">File Type</label>
                <select
                  value={settings.fileType}
                  onChange={(e) => set('fileType', e.target.value as AdvancedSettings['fileType'])}
                  className={selectClass}
                >
                  <option value="png">PNG</option>
                  <option value="jpg">JPG</option>
                  <option value="mp4">MP4</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wide mb-1.5">Image Quality</label>
                <select
                  value={settings.quality}
                  onChange={(e) => set('quality', e.target.value as AdvancedSettings['quality'])}
                  className={selectClass}
                >
                  <option value="standard">Standard</option>
                  <option value="high">High</option>
                  <option value="ultra">Ultra</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wide mb-1.5">Background</label>
              <div className="flex gap-2">
                {(['transparent', 'solid', 'original'] as const).map((bg) => (
                  <button
                    key={bg}
                    onClick={() => set('background', bg)}
                    className={`flex-1 py-2 rounded-lg border text-xs font-medium capitalize transition-all duration-200 cursor-pointer ${
                      settings.background === bg
                        ? 'bg-orange-500/15 border-orange-500/60 text-orange-400'
                        : 'bg-stone-800 border-stone-700 text-stone-400 hover:border-stone-600'
                    }`}
                  >
                    {bg}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wide mb-1.5">
                Animation Duration: <span className="text-orange-400">{settings.animDuration}s</span>
              </label>
              <input
                type="range"
                min={2}
                max={30}
                value={settings.animDuration}
                onChange={(e) => set('animDuration', parseInt(e.target.value))}
                className="w-full accent-orange-500 cursor-pointer"
              />
              <div className="flex justify-between text-xs text-stone-600 mt-1">
                <span>2s</span>
                <span>15s</span>
                <span>30s</span>
              </div>
            </div>

            <div className="bg-stone-800/40 rounded-xl px-4 py-1 border border-stone-800">
              <ToggleRow label="Compression" value={settings.compression} onChange={(v) => set('compression', v)} hint="Reduce file size for faster uploads" />
              <ToggleRow label="Safe Zone Overlay" value={settings.safeZone} onChange={(v) => set('safeZone', v)} hint="Show platform safe areas in preview" />
              <ToggleRow label="Platform Crop Guides" value={settings.cropGuides} onChange={(v) => set('cropGuides', v)} hint="Visualize how each platform will crop your ad" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
